<template>
  <div id="main-out">
    <div class="arbeitszeiten">
      <div class="header">
        <h1>Arbeitszeiten</h1>
      </div>

      <div class="time-log">
        <div class="conf-time">
          <h2>Zeiterfassungsprotokoll</h2>

          <!-- Mitarbeiter-Auswahl -->
          <select 
            v-model="selectedWorker" 
            class="worker-select" 
            @change="fetchTimeLogs">
            <option value="" disabled>Wählen Sie einen Mitarbeiter</option>
            <option 
              v-for="worker in workers" 
              :key="worker.id" 
              :value="worker.username">
              {{ worker.firstName }} {{ worker.lastName }}
            </option>
          </select>

          <!-- Datumsauswahl -->
          <input 
            type="date" 
            v-model="selectedDate" 
            class="date-picker" 
            @change="fetchTimeLogs" />
        </div>

        <!-- Anzeige der Arbeitszeiten -->
        <div v-if="timeLog && timeLog.length" class="time-details">
          <h3>Mitarbeiter: {{ selectedWorker }}</h3>
          <p>Monat: {{ new Date(selectedDate).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit' }) }}</p>

          <div v-for="(log, index) in timeLog" :key="index" class="time-entry">
            <p><strong>Datum:</strong> {{ log.date }}</p>
            <p><strong>Startzeit:</strong> {{ log.startTime }}</p>
            <p><strong>Endzeit:</strong> {{ log.endTime }}</p>

            <h4>Pausen:</h4>
            <ul>
              <li v-for="(breakTime, breakIndex) in log.breaks" :key="breakIndex">
                Pause {{ breakIndex + 1 }}: 
                {{ breakTime.start }} - {{ breakTime.end }}
              </li>
            </ul>

            <!-- Gesamtarbeitszeit -->
            <p>
              <strong>Gesamtarbeitszeit:</strong> 
              {{ calculateWorkDuration(log.startTime, log.endTime, log.breaks) }}
            </p>
            <hr />
          </div>
          
          <!-- PDF-Download-Button -->
          <button class="download-button" @click="downloadPDF">
            Herunterladen
          </button>

          <!-- Monatliche Gesamtarbeitszeit -->
          <p v-if="monthlyTotal" class="monthly-total">
            <strong class="gestime">Gesamte Arbeitszeit im Monat:</strong> {{ monthlyTotal }} Stunden
          </p>
        </div>

        <div v-else class="no-data">
          <p>Keine Arbeitszeiten für das ausgewählte Datum gefunden.</p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Für Tabellen im PDF
import { keycloak } from "../../keycloak";

axios.interceptors.request.use(
  (config) => {
    config.headers["Authorization"] = `Bearer ${keycloak.token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export default {
  setup() {
    const timeLog = ref([]);
    const workers = ref([]);
    const selectedWorker = ref('');
    const selectedDate = ref(new Date().toISOString().split('T')[0]);
    const monthlyTotal = ref(0);

    // API: Tagesdaten abrufen
    const fetchTimeLogs = async () => {
      if (!selectedWorker.value || !selectedDate.value) return;

      try {
        const response = await axios.get('/api/worker/time', {
          params: { 
            name: selectedWorker.value,
            date: selectedDate.value,
          },
        });

        // Daten verarbeiten
        const data = response.data;
        timeLog.value = data.map(log => ({
          date: formatDate(log.date),
          startTime: log.startTime || 'Keine Startzeit',
          endTime: log.endTime || 'Keine Endzeit',
          breaks: log.breaks.map(breakTime => ({
            start: breakTime.start || 'Keine Startzeit',
            end: breakTime.end || 'Keine Endzeit',
          })),
        }));
      } catch (error) {
        console.error('Fehler beim Abrufen der Tagesdaten:', error);
        timeLog.value = [];
      }
    };

    // Hilfsfunktion: Datumsformatierung
    const formatDate = (date) => {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate)) {
        console.error('Ungültiges Datum:', date);
        return 'Ungültiges Datum';
      }
      return parsedDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Arbeitszeit berechnen
    const calculateWorkDuration = (startTime, endTime, breaks) => {
      const start = new Date(`1970-01-01T${startTime}`);
      const end = new Date(`1970-01-01T${endTime}`);
      const totalBreaks = breaks.reduce((sum, b) => {
        const breakStart = new Date(`1970-01-01T${b.start}`);
        const breakEnd = new Date(`1970-01-01T${b.end}`);
        return sum + (breakEnd - breakStart);
      }, 0);

      const workDuration = (end - start - totalBreaks) / 3600000; // In Stunden
      return `${workDuration.toFixed(2)} Stunden`;
    };

    // Monatliche Gesamtarbeitszeit berechnen
    const calculateMonthlyTotal = () => {
      if (!timeLog.value || !timeLog.value.length) {
        monthlyTotal.value = 0;
        return;
      }

      const totalHours = timeLog.value.reduce((sum, log) => {
        const workDuration = parseFloat(
          calculateWorkDuration(log.startTime, log.endTime, log.breaks).replace(' Stunden', '')
        );
        return sum + workDuration;
      }, 0);

      monthlyTotal.value = totalHours.toFixed(2);
    };

    // PDF-Download
    const downloadPDF = () => {
      const doc = new jsPDF();

      const workerName = workers.value.find(
        (worker) => worker.username === selectedWorker.value
      );
      const selectedMonthYear = new Date(selectedDate.value).toLocaleDateString('de-DE', { 
        year: 'numeric', 
        month: '2-digit' 
      });

      const title = `Zeiterfassungsprotokoll vom ${selectedMonthYear} von ${workerName.firstName} ${workerName.lastName}`;

      doc.setTextColor(255, 204, 0);
      doc.setFontSize(16);
      doc.text(title, 10, 10);

      const tableData = timeLog.value.map((log, index) => [
        log.date,
        log.startTime,
        log.endTime,
        calculateWorkDuration(log.startTime, log.endTime, log.breaks),
        log.breaks.map(b => `${b.start} - ${b.end}`).join(', '),
      ]);

      doc.autoTable({
        startY: 20,
        head: [['Datum', 'Startzeit', 'Endzeit', 'Gesamtarbeitszeit', 'Pausen']],
        body: tableData,
        styles: { halign: 'center' },
        headStyles: { fillColor: [255, 204, 0] },
      });

      doc.save('Arbeitszeiten.pdf');
    };

    // API: Mitarbeiterliste abrufen
    const fetchWorkers = async () => {
      try {
        const response = await axios.get('/api/worker');
        workers.value = response.data;
      } catch (error) {
        console.error('Fehler beim Abrufen der Mitarbeiter:', error);
      }
    };

    onMounted(() => {
      fetchWorkers();
      fetchTimeLogs();
    });

    watch(timeLog, calculateMonthlyTotal);

    return {
      workers,
      timeLog,
      selectedWorker,
      selectedDate,
      fetchTimeLogs,
      calculateWorkDuration,
      downloadPDF,
      formatDate,
      monthlyTotal,
    };
  },
};
</script>
<style scoped>
.gestime{
  color: white;
}
#main-out {
  height: 100vh;
}
.conf-time {
  display: flex;
  align-items: center;
  gap: 40px;
}
.download-button {
  padding: 10px 20px;
  border-radius: 8px;
  background-color: #ffd700; /* Gelb */
  border: none;
  color: black;
  cursor: pointer;
  font-weight: bold;
}
.date-picker {
  padding: 10px;
  font-size: 1rem;
  border-radius: 4px;
  border: 1px solid #374151;
  background-color: #1f2937;
  color: #d1d5db;
}
.worker-select {
  margin: 10px 0;
  padding: 10px;
  font-size: 1rem;
  border-radius: 4px;
  border: 1px solid #374151;
  background-color: #1f2937;
  color: #d1d5db;
}
.time-details {
  margin-top: 20px;
}
.time-entry {
  margin-bottom: 10px;
}
.monthly-total {
  margin-top: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}
</style>
